<template>
  <header class="appbar" :class="{ 'is-wallet': isWallet || isEarn, 'is-me-sub': isMeSub }">
    <!-- 充值中心 / 代理赚钱：左右等宽槽位夹标题，无需绝对定位 -->
    <template v-if="isWallet || isEarn">
      <div class="ab-side ab-side-start">
        <button
          v-if="hasWalletBack"
          type="button"
          class="wx2-back"
          :aria-label="t('common.back')"
          @click="onWalletBack"
        >
          ‹
        </button>
        <div v-else class="brand">
          <span class="mark">B</span>
          <span class="name">Bet<b>Corgi</b></span>
        </div>
      </div>
      <div class="ab-title">{{ centerTitle }}</div>
      <div class="ab-side ab-side-end">
        <button
          v-if="hasWalletBack && supportEnabled"
          type="button"
          class="wx2-cs"
          :aria-label="t('common.support')"
          @click="onSupport"
        >
          <AppIcon name="headset" class="ico" />
        </button>
      </div>
    </template>

    <template v-else-if="isMeSub">
      <div class="ab-side ab-side-start">
        <button type="button" class="sub-back" :aria-label="t('common.back')" @click="onMeSubBack">
          ‹
        </button>
      </div>
      <div class="ab-title">{{ meSubTitle }}</div>
      <div class="ab-side ab-side-end" />
    </template>

    <!-- 首页 / 奖励 / 我的 -->
    <template v-else>
      <div class="brand">
        <span class="mark">B</span>
        <span class="name">Bet<b>Corgi</b></span>
      </div>
      <div class="spacer" />

      <template v-if="isMe">
        <button
          v-if="userStore.isLoggedIn"
          type="button"
          class="ab-ico me-bell"
          :aria-label="t('common.notice')"
          @click="onNotice"
        >
          <AppIcon name="bell" class="ico" />
          <i v-if="userStore.noticeUnread > 0" class="nb-dot">
            {{ noticeBadge }}
          </i>
        </button>
        <button
          v-if="supportEnabled"
          type="button"
          class="ab-ico me-cs"
          :aria-label="t('common.support')"
          @click="onSupport"
        >
          <AppIcon name="headset" class="ico" />
        </button>
      </template>

      <ClientOnly>
        <div v-if="userStore.isLoggedIn && showBalance" class="balance-pill">
          <span class="bal-cur">
            <span class="bal-ic">
              <img :src="userStore.currencyIcon" alt="" />
            </span>
            <span>{{ displayBalanceText }}</span>
          </span>
          <button type="button" class="add" :aria-label="t('common.deposit')" @click="onDeposit">
            +
          </button>
        </div>

        <div v-else-if="!userStore.isLoggedIn" class="auth-guest">
          <button type="button" class="btn-reg" @click="onLogin">
            {{ t('common.loginRegister') }}
          </button>
        </div>
      </ClientOnly>
    </template>
  </header>
</template>

<script setup lang="ts">
import { formatAvailableDisplay } from '~/utils/transfer/money'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const userStore = useUserStore()

/** 顶部余额用 available_display 原样展示（后端几位就几位，不四舍五入） */
const displayBalanceText = computed(() => formatAvailableDisplay(userStore.wallet.balance))
const { walletFrom, openWalletPushed, backFromWallet } = useWalletNav()
const { title: meSubTitleState, back: meSubBack } = useMeSubNav()
const { openLogin } = useAuthNav()

const { openNotice } = useNoticeNav()
// 客服按钮：后台「站点设置 → 客服配置」没开通时不显示
const { supportEnabled, openSupport } = useSupport()

type TabKey = 'home' | 'promo' | 'wallet' | 'earn' | 'me'

const tabPaths: Record<Exclude<TabKey, 'home'>, string> = {
  promo: '/promo',
  wallet: '/wallet',
  earn: '/earn',
  me: '/me',
}

const matchPath = (to: string, path: string) => {
  const target = localePath(to)
  return path === target || path.startsWith(`${target}/`)
}

const matchTab = (key: TabKey, path: string) => {
  if (key === 'home') {
    const home = localePath('/')
    return path === home || path === `${home}/`
  }
  return matchPath(tabPaths[key], path)
}

const currentTab = computed<TabKey>(() => {
  const path = route.path
  const order: TabKey[] = ['promo', 'wallet', 'earn', 'me']
  return order.find(key => matchTab(key, path)) ?? 'home'
})

/** 首页 / 奖励显示余额；充值 / 赚钱 / 我的隐藏 */
const showBalance = computed(() => currentTab.value === 'home' || currentTab.value === 'promo')
const isWallet = computed(() => currentTab.value === 'wallet')
const isEarn = computed(() => currentTab.value === 'earn')
/** 顶栏居中文案：充值中心 / 代理赚钱，与左右槽位同一套布局 */
const centerTitle = computed(() => (isEarn.value ? t('earn.title') : t('common.walletCenter')))

const isMe = computed(() => currentTab.value === 'me')

/** 我的 tab 下路由二级页（抽屉型二级页自带 sub-head，不走此处） */
const isMeSub = computed(() => {
  if (!isMe.value) return false
  const base = localePath('/me')
  const path = route.path
  return path !== base && path !== `${base}/`
})

const meSubTitle = computed(() => meSubTitleState.value)

/**
 * 推入态显示返回：页内「+」入口，或 wallet 子路径
 * （底导进根页无返回；页内入口带返回）
 */
const hasWalletBack = computed(() => {
  if (!isWallet.value) return false
  if (walletFrom.value) return true
  const base = localePath('/wallet')
  const path = route.path
  return path !== base && path !== `${base}/`
})

const noticeBadge = computed(() =>
  userStore.noticeUnread > 99 ? '99+' : String(userStore.noticeUnread),
)

const onLogin = () => {
  openLogin()
}

const onDeposit = () => {
  openWalletPushed(route.fullPath)
}

const onWalletBack = () => {
  backFromWallet()
}

const onMeSubBack = () => {
  if (meSubBack()) return
  navigateTo(localePath('/me'))
}

const onNotice = () => {
  openNotice()
}

const onSupport = () => {
  openSupport()
}
</script>

<style scoped lang="scss">
.appbar {
  position: fixed;
  top: 0;
  /* Chrome 85：避免 fixed + transform 合成层导致品牌名出现灰底矩形 */
  left: 0;
  right: 0;
  z-index: 40;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  /* constant 供旧 iOS；env 供 Chrome 85+（constant 双参写法不稳定，勿带 fallback） */
  padding: 10px calc(14px + constant(safe-area-inset-right)) 10px
    calc(14px + constant(safe-area-inset-left));
  padding: 10px calc(14px + env(safe-area-inset-right, 0px)) 10px
    calc(14px + env(safe-area-inset-left, 0px));
  padding-top: calc(10px + constant(safe-area-inset-top));
  padding-top: calc(10px + env(safe-area-inset-top, 0px));
  background: var(--bg);
  border-bottom: 1px solid var(--hairline);
  min-height: calc(var(--appbar-h) + constant(safe-area-inset-top));
  min-height: calc(var(--appbar-h) + env(safe-area-inset-top, 0px));
  box-sizing: border-box;

  &.is-wallet {
    justify-content: flex-start;
  }

  &.is-me-sub {
    gap: 10px;
  }
}

.brand {
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 800;
  font-size: 18px;
  letter-spacing: 0.3px;
  background: transparent;
  -webkit-tap-highlight-color: transparent;

  .mark {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 900;
    font-size: 15px;
    background: var(--brand-mark);
    box-shadow: var(--brand-glow);
  }

  .name {
    background: transparent;
  }

  .name b {
    color: var(--primary);
    background: transparent;
    font-weight: 800;
  }
}

.spacer {
  flex: 1;
}

.balance-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--surface2);
  border: 1px solid var(--hairline);
  padding: 5px 6px 5px 10px;
  border-radius: var(--r-xl);
  font-weight: 700;
  font-size: 13px;

  .bal-cur {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text);
  }

  .bal-ic {
    display: inline-flex;

    img {
      width: 16px;
      height: 16px;
      display: block;
    }
  }

  .add {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--primary);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    padding: 0;
    border: 0;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
  }
}

.btn-reg {
  padding: 7px 16px;
  border-radius: var(--r-pill);
  background: var(--primary);
  border: 0;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  box-shadow: var(--btn-glow);
}

.ab-ico {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 3px;
  color: var(--text);
  position: relative;
  background: none;
  border: none;

  .ico {
    font-size: 22px;
  }

  &:active {
    transform: scale(0.94);
  }

  &.me-cs {
    margin-left: 14px;
  }
}

.nb-dot {
  position: absolute;
  top: 0;
  right: -3px;
  min-width: 15px;
  height: 15px;
  line-height: 15px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  font-style: normal;
  color: #fff;
  text-align: center;
  padding: 0 3.5px;
  background: var(--danger);
}

.ab-side {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.ab-side-start {
  justify-content: flex-start;
}

.ab-side-end {
  justify-content: flex-end;
}

.ab-title {
  flex: 0 0 auto;
  font-size: 16px;
  font-weight: 900;
  color: var(--text);
  white-space: nowrap;
  text-align: center;
  background: transparent;
}

.wx2-back {
  display: block;
  width: 34px;
  height: 30px;
  padding: 0;
  font-size: 24px;
  font-weight: 800;
  color: var(--text);
  background: none;
  border: 0;
  text-align: left;
  flex: 0 0 auto;
}

.sub-back {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: var(--r-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  line-height: 1;
  background: var(--surface2);
  border: 1px solid var(--hairline);
  color: var(--text);
  padding: 0;
  -webkit-appearance: none;
  appearance: none;

  &:active {
    opacity: 0.85;
  }
}

.wx2-cs {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 30px;
  padding: 0;
  background: none;
  border: 0;
  color: var(--text);
  cursor: pointer;
  flex: 0 0 auto;
  -webkit-appearance: none;
  appearance: none;

  .ico {
    font-size: 22px;
  }

  &:active {
    transform: scale(0.94);
  }
}
</style>
